import { ObjectdeviceDevice, ObjectobjectObject, ObjectroomRoom } from '@/types/api'

export type DeviceFilterOption = {
  value: string | number
  count: number
  label?: string
  active?: boolean
}

export type DeviceInstallationFilter = {
  label: 'installed' | 'uninstalled'
  value: string
} | null

export type DeviceFiltersState = {
  searchCategories: DeviceFilterOption[]
  searchRoom: DeviceFilterOption[]
  searchText: string
  searchRooms: DeviceFilterOption[]
  searchInstallation: DeviceInstallationFilter
  searchFloor: DeviceFilterOption[]
  searchTenant: DeviceFilterOption[]
  searchOwner: DeviceFilterOption[]
  searchAssignee: DeviceFilterOption[]
}

export type BuildDeviceFiltersInput = {
  device?: ObjectdeviceDevice | null
  object?: ObjectobjectObject | null
  room?: ObjectroomRoom | null
  deviceTypeLabel?: string
  installedLabel?: string
  uninstalledLabel?: string
  tenantContactIds?: string[]
  assigneeContactId?: string | null
}

function option(value: string | number): DeviceFilterOption {
  return { value, count: 1 }
}

type DateObjectLike = {
  year?: number
  month?: number
  day?: number
}

/**
 * Parse uninstallation the same way device list filters do (via objectToDateInput semantics).
 * null / empty / year sentinel (-1) → no date.
 */
export function getDeviceUninstallationTimestamp(
  device: Pick<ObjectdeviceDevice, 'uninstallation'>
): number | null {
  const uninstallation = device.uninstallation
  if (uninstallation == null || typeof uninstallation !== 'object') {
    return null
  }
  const dateObj = uninstallation as DateObjectLike
  if (Object.keys(dateObj).length === 0) {
    return null
  }
  const year = dateObj.year
  // Match objectToDateInput: year === -1 / missing / non-positive → empty date
  if (year == null || year === -1 || year <= 0) {
    return null
  }
  const month = dateObj.month ?? 1
  const day = dateObj.day ?? 1
  const timestamp = new Date(
    String(year).padStart(4, '0') + '-' + ('0' + month).slice(-2) + '-' + ('0' + day).slice(-2)
  ).getTime()
  return Number.isNaN(timestamp) ? null : timestamp
}

/**
 * Aligns with filterDevicesList: installed when there is no uninstall date, or it is in the future.
 */
export function isDeviceInstalled(
  device: Pick<ObjectdeviceDevice, 'uninstallation'>,
  now = Date.now()
): boolean {
  const uninstallationDate = getDeviceUninstallationTimestamp(device)
  return uninstallationDate == null || uninstallationDate > now
}

/**
 * Aligns with filterDevicesList: uninstalled when uninstall date exists and is in the past.
 */
export function isDeviceUninstalled(
  device: Pick<ObjectdeviceDevice, 'uninstallation'>,
  now = Date.now()
): boolean {
  const uninstallationDate = getDeviceUninstallationTimestamp(device)
  return uninstallationDate != null && uninstallationDate < now
}

export function emptyDeviceFilters(): DeviceFiltersState {
  return {
    searchCategories: [],
    searchRoom: [],
    searchText: '',
    searchRooms: [],
    searchInstallation: null,
    searchFloor: [],
    searchTenant: [],
    searchOwner: [],
    searchAssignee: []
  }
}

export function hasDeviceFilters(filters: DeviceFiltersState): boolean {
  return (
    filters.searchText !== '' ||
    filters.searchInstallation !== null ||
    filters.searchCategories.length > 0 ||
    filters.searchRooms.length > 0 ||
    filters.searchRoom.length > 0 ||
    filters.searchFloor.length > 0 ||
    filters.searchAssignee.length > 0 ||
    filters.searchTenant.length > 0 ||
    filters.searchOwner.length > 0
  )
}

/**
 * Build Components & Devices filter state from a ticket connection payload.
 * Only sets filters when source data is available.
 */
export function buildDeviceFiltersFromConnection(input: BuildDeviceFiltersInput): DeviceFiltersState {
  const filters = emptyDeviceFilters()
  const { device, object, room, deviceTypeLabel, installedLabel, uninstalledLabel, tenantContactIds, assigneeContactId } = input

  if (deviceTypeLabel) {
    filters.searchText = deviceTypeLabel
  } else if (device?.brand) {
    filters.searchText = device.brand
  } else if (device?.model) {
    filters.searchText = device.model
  }

  if (room?.name) {
    filters.searchRoom = [option(room.name)]
  }

  if (device) {
    // Prefer uninstalled only for past dates; null/sentinel/future (and today) → installed
    filters.searchInstallation = isDeviceUninstalled(device)
      ? { label: 'uninstalled', value: uninstalledLabel || 'Uninstalled' }
      : { label: 'installed', value: installedLabel || 'Installed' }
  }

  const floor = object?.characteristics?.floor
  if (floor !== undefined && floor !== null && floor !== -10000 && String(floor) !== 'undefined') {
    filters.searchFloor = [option(floor)]
  }

  const rooms = object?.characteristics?.rooms
  if (rooms !== undefined && rooms !== null && rooms !== -1 && String(rooms) !== 'undefined') {
    filters.searchRooms = [option(rooms)]
  }

  if (tenantContactIds && tenantContactIds.length > 0) {
    filters.searchTenant = tenantContactIds.filter(Boolean).map(id => option(id))
  }

  if (assigneeContactId) {
    filters.searchAssignee = [option(assigneeContactId)]
  }

  return filters
}

export function serializeDeviceFilters(filters: DeviceFiltersState): string {
  return JSON.stringify(filters)
}

export function buildDeviceDetailQuery(
  deviceId: string,
  filters?: DeviceFiltersState | null
): { view: string, elementSelected: string, deviceFilters?: string } {
  const query: { view: string, elementSelected: string, deviceFilters?: string } = {
    view: 'devices',
    elementSelected: deviceId
  }
  if (filters && hasDeviceFilters(filters)) {
    query.deviceFilters = serializeDeviceFilters(filters)
  }
  return query
}

export function getDeviceAssigneePropKey(moduleFlags: {
  isAccountingModule?: boolean
  isAdminModule?: boolean
  isAssetModule?: boolean
}): 'contactIdAssigneeAccounting' | 'contactIdAssigneeAdministration' | 'contactIdAssigneeAsset' | 'contactIdAssigneeOwner' {
  if (moduleFlags.isAccountingModule) {
    return 'contactIdAssigneeAccounting'
  }
  if (moduleFlags.isAdminModule) {
    return 'contactIdAssigneeAdministration'
  }
  if (moduleFlags.isAssetModule) {
    return 'contactIdAssigneeAsset'
  }
  return 'contactIdAssigneeOwner'
}
